import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { weddingGallery, siteInfo } from "../data/mockData";
import { ArrowRight, Heart, Camera, X, ChevronLeft, ChevronRight } from "lucide-react";
import useInView from "../hooks/useInView";

const Weddings = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [galleryRef, galleryInView] = useInView();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1920&q=80)",
          }}
        >
          <div className="absolute inset-0 bg-[#1B2A4A]/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">
            Your Special Day
          </span>
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mt-3">
            Weddings at Willow Ridge
          </h1>
          <p className="text-white/70 text-lg mt-4 max-w-xl">
            Say "I do" surrounded by countryside beauty, elegant décor, and
            exceptional service that makes every moment magical.
          </p>
        </div>
      </section>

      {/* About Weddings */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="overflow-hidden rounded-2xl shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=800&q=80"
                alt="Wedding at Willow Ridge"
                className="w-full h-[500px] object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div>
              <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">
                Elegant & Memorable
              </span>
              <h2 className="text-4xl font-serif font-bold text-[#1B2A4A] mt-3 mb-6">
                Your Dream Wedding Awaits
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Our elegant, air-conditioned banquet hall is fully decorated to
                match your wedding theme. With countryside views, mature trees,
                and manicured lawns as your backdrop, every photo will be
                picture-perfect.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                We accommodate 30 to 200 seated guests and our experienced team
                handles every detail from décor to custom menu planning,
                ensuring your special day is stress-free and magical.
              </p>
              <div className="space-y-3">
                {[
                  "Décor rentals to match your theme",
                  "Wedding planning advice & consultation",
                  "Custom menu planning with chef",
                  "White linen tablecloths & napkins",
                  "Microphone, podium & TV screen",
                  "Ample free parking for guests",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#C9A96E]/10 flex items-center justify-center shrink-0">
                      <Heart size={12} className="text-[#C9A96E]" />
                    </div>
                    <span className="text-gray-600 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Catering Services */}
      <section className="py-24 bg-[#f8f7f4]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">
                Catering Services
              </span>
              <h2 className="text-4xl font-serif font-bold text-[#1B2A4A] mt-3 mb-6">
                Exceptional Catering for Every Occasion
              </h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                Willow Ridge Golf & Country Club is proud to offer our exceptional catering services throughout the Chatham-Kent area and beyond.
              </p>
              <p className="text-gray-600 leading-relaxed mb-5">
                With a diverse range of menu options, we are committed to delivering professional catering that aligns with your unique vision, needs, and budget. Our dedication to quality and customer service ensures an experience marked by professionalism and excellence.
              </p>
              <p className="text-gray-600 leading-relaxed mb-5">
                If you don't see exactly what you're looking for on our menu, our Executive Chef is happy to collaborate with you to create a customized menu that perfectly reflects your vision, dietary preferences, and budget.
              </p>
              <p className="text-gray-500 text-sm italic mb-6">
                Please note, a delivery fee may apply for offsite catering depending on the location.
              </p>
              <p className="text-gray-600 leading-relaxed">
                We truly appreciate your consideration of Willow Ridge Golf & Country Club for your special event catering needs.
              </p>
            </div>
            <div className="overflow-hidden rounded-2xl shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1523438885200-e635ba2c371e?w=800&q=80"
                alt="Catering at Willow Ridge"
                className="w-full h-[500px] object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section ref={galleryRef} className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">
              Gallery
            </span>
            <h2 className="text-4xl font-serif font-bold text-[#1B2A4A] mt-3">
              Past Weddings
            </h2>
          </div>
          <div className={`grid grid-cols-2 md:grid-cols-3 gap-4 ${
            galleryInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          } transition-all duration-700`}>
            {weddingGallery.map((img, index) => (
              <div
                key={index}
                className="relative group overflow-hidden rounded-xl cursor-pointer aspect-[4/3]"
                onClick={() => openLightbox(index)}
              >
                <img
                  src={img}
                  alt={`Wedding ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-[#1B2A4A]/0 group-hover:bg-[#1B2A4A]/40 transition-colors duration-500 flex items-center justify-center">
                  <Camera
                    size={32}
                    className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-6"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            className="absolute top-6 right-6 text-white hover:text-[#C9A96E] transition-colors z-10"
            onClick={() => setLightboxOpen(false)}
          >
            <X size={32} />
          </button>
          <button
            className="absolute left-6 top-1/2 -translate-y-1/2 text-white hover:text-[#C9A96E] transition-colors z-10"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIndex((lightboxIndex - 1 + weddingGallery.length) % weddingGallery.length);
            }}
          >
            <ChevronLeft size={40} />
          </button>
          <button
            className="absolute right-6 top-1/2 -translate-y-1/2 text-white hover:text-[#C9A96E] transition-colors z-10"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIndex((lightboxIndex + 1) % weddingGallery.length);
            }}
          >
            <ChevronRight size={40} />
          </button>
          <img
            src={weddingGallery[lightboxIndex]}
            alt={`Wedding ${lightboxIndex + 1}`}
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* CTA */}
      <section className="py-20 bg-[#1B2A4A]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <Heart size={32} className="text-[#C9A96E] mx-auto mb-4" />
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">
            Begin Your Forever Here
          </h2>
          <p className="text-white/60 mb-8">
            Contact Shannon Thyssen, Food & Beverage Manager, to start
            planning your dream wedding at Willow Ridge.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#C9A96E] text-[#1B2A4A] font-semibold rounded-md hover:bg-[#d4b97e] transition-colors duration-300"
          >
            Schedule a Tour <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Weddings;
